import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError, first } from 'rxjs/operators';
import { AppSettings } from '../app-settings';
import { BehaviorSubject, of } from 'rxjs';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class FavoritosService {

  private modified = new BehaviorSubject<any>(false);
  public modObs = this.modified.asObservable();

  // Lista de IDs de eventos favoritos del usuario
  private favoritosIds = new BehaviorSubject<Set<number>>(new Set());
  public favoritosIds$ = this.favoritosIds.asObservable();

  constructor(private http: HttpClient, private auth: AuthenticationService) {
    // Suscribirse a cambios en el perfil para recargar favoritos cuando el usuario inicie sesión
    this.auth.profileStorage.subscribe(profile => {
      if (profile && profile.id) {
        this.loadFavoritosIds();
      } else {
        // Si el usuario cierra sesión, limpiar la lista de favoritos
        this.favoritosIds.next(new Set());
      }
    });
  }

  setMod() {
    this.modified.next(false);
  }

  // Cargar la lista de IDs de favoritos
  loadFavoritosIds() {
    const url = `${ AppSettings.API_URI_V2 }/user/favorites?items=1000&page=1`;
    this.http.get<any>(url).pipe(
      map(response => {
        if (response && response.data) {
          return response.data.map((evento: any) => evento.id);
        }
        return [];
      }),
      catchError(() => of([]))
    ).subscribe(ids => {
      this.favoritosIds.next(new Set(ids));
    });
  }

  // Verificar si un evento está en favoritos
  isFavorito(eventId: number): boolean {
    return this.favoritosIds.value.has(eventId);
  }

  // Agregar un ID a la lista
  private addFavoritoId(eventId: number) {
    const current = this.favoritosIds.value;
    current.add(eventId);
    this.favoritosIds.next(new Set(current));
  }

  // Remover un ID de la lista
  private removeFavoritoId(eventId: number) {
    const current = this.favoritosIds.value;
    current.delete(eventId);
    this.favoritosIds.next(new Set(current));
  }

  // Nuevo endpoint para obtener favoritos del usuario
  getFavoritos(items: any = 12, page: any = 1) {
    const url = `${ AppSettings.API_URI_V2 }/user/favorites?items=${items}&page=${page}`;
    return this.http.get<any>( url ).pipe(
      map(response => {
        let eventos = [];
        // La API devuelve {success, message, data: [...eventos]}
        if (response && response.data) {
          eventos = response.data;
        }
        // Formato Hydra alternativo
        else if (response && response['hydra:member']) {
          eventos = response['hydra:member'];
        }
        // Array directo
        else if (Array.isArray(response)) {
          eventos = response;
        }

        // Agregar el usuario actual al array favoritos de cada evento
        // y crear sesiones mock basadas en las fechas del evento
        const userId = this.auth.user;
        eventos = eventos.map(evento => {
          const eventoProcessed = {
            ...evento,
            favoritos: userId ? (evento.favoritos || [{ id: userId }]) : (evento.favoritos || [])
          };

          // Si el evento no tiene sesiones, crear una mock basada en start_date
          if (!eventoProcessed.sessions || eventoProcessed.sessions.length === 0) {
            eventoProcessed.sessions = [{
              id: evento.id,
              start_date: evento.start_date,
              end_date: evento.end_date,
              id_event: evento.id
            }];
          }

          return eventoProcessed;
        });

        return eventos;
      }), catchError((err) => {
        return of([]);
      })
    );
  }

  getFavoritosLast(items: any = 12, page: any = 1) {
    const url = `${ AppSettings.API_URI_V2 }/user/favorites?items=${items}&page=${page}&past=true`;
    return this.http.get<any>( url ).pipe(
      map(response => {
        let eventos = [];
        // La API devuelve {success, message, data: [...eventos]}
        if (response && response.data) {
          eventos = response.data;
        }
        // Formato Hydra alternativo
        else if (response && response['hydra:member']) {
          eventos = response['hydra:member'];
        }
        // Array directo
        else if (Array.isArray(response)) {
          eventos = response;
        }

        // Agregar el usuario actual al array favoritos de cada evento
        // y crear sesiones mock basadas en las fechas del evento
        const userId = this.auth.user;
        eventos = eventos.map(evento => {
          const eventoProcessed = {
            ...evento,
            favoritos: userId ? (evento.favoritos || [{ id: userId }]) : (evento.favoritos || [])
          };

          // Si el evento no tiene sesiones, crear una mock basada en start_date
          if (!eventoProcessed.sessions || eventoProcessed.sessions.length === 0) {
            eventoProcessed.sessions = [{
              id: evento.id,
              start_date: evento.start_date,
              end_date: evento.end_date,
              id_event: evento.id
            }];
          }

          return eventoProcessed;
        });

        return eventos;
      }), catchError((err) => {
        return of([]);
      })
    );
  }

  // Nuevo endpoint toggle para agregar/eliminar favoritos
  toggleFavorito(eventId: any) {
    const url = `${ AppSettings.API_URI_V2 }/events/${eventId}/toggle-favorite`;
    const wasFavorito = this.isFavorito(eventId);

    return this.http.post<any>(url, {}).pipe(
      map(data => {
        if(data) {
          // Actualizar la lista de IDs según la acción realizada
          if (wasFavorito) {
            this.removeFavoritoId(eventId);
          } else {
            this.addFavoritoId(eventId);
          }
          this.modified.next(eventId);
          return data;
        }
        return of(false);
      }), catchError((err) => {
        return of(false);
      })
    );
  }

  // Mantener compatibilidad - ahora usan toggle
  postFavoritos(eventId: any) {
    return this.toggleFavorito(eventId);
  }

  deleteFavorito(eventId: number) {
    return this.toggleFavorito(eventId);
  }
}

